<template>
  <div id="mapContainer" class="basic-map">
    <div ref="pointPopup" class="point-popup">
      <slot name="pointPopup" :data="activePointInfo"></slot>
    </div>
  </div>
</template>

<script>
import hmap from 'hmap'
import EventType from 'EventType'
import { mapUtil } from '@/components/TownPanel/BasicMap/mapUtils'

const defaultOption = {
  zoom: 16,
  maxLevel: 18,
  minLevel: 11
}
const defaultClusterOption = {
  opacity: 1.0,
  enableHashCode: true,
  clusterOne: false,
  gridSize: 256,
  disableClusteringAtZoom: 16
}

export default {
  name: 'HMap',
  props: {
    options: {
      type: Object,
      validator: function(val) {
        return !!val.center
      },
      default: () => ({})
    },
    layerOptions: {
      type: Array,
      default: () => []
    },
    clusterOption: {
      default: () => ({ enable: false }),
      type: Object,
      validator: function(val) {
        if (val.enable && !val.clusterStyle) {
          return false
        }
        return true
      }
    },
    needBoundLayer: {
      type: Boolean,
      default: false
    },
    needPointLayer: {
      type: Boolean,
      default: false
    },
    disableDetectFromMap: {
      type: Boolean,
      default: false
    },
    enablePointHover: {
      type: Boolean,
      default: false
    }
  },
  data() {
    this.mapInstance = null
    this.popupInstance = null
    this.layers = {
      layerArr: [],
      boundLayer: null, // 边界要素图层
      pointLayer: null, // 点位图层
      clusterLayer: null, // 聚合图层
      townExtent: null
    }
    this.activePointCache = {
      point: null,
      style: null
    }
    return {
      mapOptions: {},
      activePointInfo: {}
    }
  },
  created() {
    mapUtil.setCrs('900913')
    this.mapOptions = Object.assign({}, defaultOption, this.options)
  },
  mounted() {
    this.initMap()
  },
  methods: {
    initMap() {
      this.mapInstance = new hmap.Map('mapContainer', this.mapOptions)
      this.$emit('mapInited', this.mapInstance, this.layers)
      this.initLayer()
      this.needBoundLayer && this.initBoundLayer()
      this.needPointLayer && this.initPointLayer()
      this.initPointPopup()
      this.initMapEvent()
    },
    /**
     * 通过layerOptions初始化图层
     */
    initLayer() {
      this.layers.layerArr = this.layerOptions.map((option, ind) => {
        const { name, type, url, layerOption } = option
        let layer
        if (type === 'TMS') {
          layer = new hmap.layer.TMSLayer(name, url, layerOption)
          layer.setZIndex(ind)
        } else if (type === 'VectorTile') {
          layer = new hmap.layer.VectorTileLayer(name, url, layerOption)
          layer.setZIndex(ind)
        }
        return layer
      })
      this.mapInstance.addLayers(this.layers.layerArr)
    },
    initBoundLayer() {
      this.layers.boundLayer = new hmap.layer.VectorLayer('边界图层', {
        enableHashCode: false,
        isRTE: true
      })
      this.mapInstance.addLayer(this.layers.boundLayer, {})
    },
    initPointLayer() {
      if (this.clusterOption.enable) {
        const option = Object.assign({}, defaultClusterOption, this.clusterOption)
        this.layers.clusterLayer = new hmap.layer.ClusterLayer('聚合图层', option)
        this.mapInstance.addLayer(this.layers.clusterLayer)
      }
      this.layers.pointLayer = new hmap.layer.VectorLayer('点位图层', {
        enableHashCode: true,
        opacity: 1
      })
      this.mapInstance.addLayer(this.layers.pointLayer)
    },
    initPointPopup() {
      const overlayLayer = new hmap.layer.OverlayLayer('点位弹窗图层')
      this.mapInstance.addLayer(overlayLayer)
      this.popupInstance = new hmap.overlay.SimplePopup({
        location: new hmap.basetype.Coordinate(120, 30, 0),
        domId: 'pointPopup',
        element: this.$refs.pointPopup,
        editable: false
      })
      overlayLayer.addOverlay(this.popupInstance)
      this.popupInstance.setVisibility(false)
    },
    initMapEvent() {
      if (this.disableDetectFromMap) return
      // 点位点击事件
      this.mapInstance.event.register(EventType.MAPMOUSEEVENT_CLICK, ev => {
        // const { mapPixel, lonlat } = ev
        const { mapPixel } = ev
        const features = this.mapInstance.detectFeaturesAtPixel(mapPixel, ['点位图层'])
        // const features = this.mapInstance.detectFeaturesAtPixel(mapPixel, ['聚合图层'])
        if (features && features.length) {
          // const { attr } = features[0].getAttributes()
          // if (attr.cluster) {
          //   const curZoom = this.mapInstance.getZoom()
          //   this.mapInstance.setCenter(lonlat, curZoom + 1)
          //   this.$emit('mapChange')
          // } else {
          this.$emit('pointClick', ev, features)
          // }
        }
        // const clusterFeatures = this.mapInstance.detectFeaturesAtPixel(mapPixel, ['聚合图层'])
        // if (clusterFeatures && clusterFeatures.length) {
        //   const fid = clusterFeatures[0].getFid()
        //   const clusted = this.layers.clusterLayer.getClusterFeaturesByFid(fid)
        //   if (clusted && clusted.length) {
        //     const curZoom = this.mapInstance.getZoom()
        //     this.mapInstance.setCenter(lonlat, curZoom + 1)
        //   } else {
        //     this.$emit('pointClick', ev, clusterFeatures)
        //     this.handleShowPopup(clusterFeatures[0])
        //   }
        // } else if (features && features.length) {
        //   this.$emit('pointClick', ev, features)
        //   this.handleShowPopup(features[0])
        // }
      })
      // 点位hover
      this.enablePointHover &&
        this.mapInstance.event.register(EventType.MAPMOUSEEVENT_MOUSEMOVE, ev => {
          const { mapPixel } = ev
          this.handleHidePopup()
          const features = this.mapInstance.detectFeaturesAtPixel(mapPixel, ['点位图层'])
          // const features = this.mapInstance.detectFeaturesAtPixel(mapPixel, ['聚合图层'])
          if (features && features.length) {
            this.handleShowPopup(features[0])
          }
        })
      this.mapInstance.event.addListener(EventType.MAPEVENT_MOVEEND, ev => {
        this.$emit('mapChange', ev)
      })
      this.mapInstance.event.addListener(EventType.MAPEVENT_ZOOMEND, ev => {
        window.console.log(this.mapInstance.getZoom())
        this.$emit('mapZoom', ev)
      })
    },
    handleShowPopup(feature) {
      if (!feature.getAttributes().longitude || !feature.getAttributes().latitude) return
      this.activePointInfo = feature.getAttributes()
      this.popupInstance.setVisibility(true)
      this.popupInstance.setLocation(
        mapUtil.coord(this.activePointInfo.longitude, this.activePointInfo.latitude)
      )
      this.$nextTick(() => {
        let iconHeight = 0
        try {
          iconHeight = feature
            .getStyle()
            .getMarkerSymbols()[0]
            .getSize()
            .getHeight()
        } catch (error) {
          window.console.log('get icon height fail:', error)
        }
        const clientRect = this.$refs.pointPopup.getBoundingClientRect()
        this.popupInstance.setOffset(
          mapUtil.offset(-clientRect.width / 2, -clientRect.height - 2 - iconHeight)
        )
      })
    },
    handleHidePopup() {
      const pointLayer = this.mapInstance.getLayersByName('点位图层')[0]
      if (this.activePointCache.point) {
        this.activePointCache.point.setStyle(this.activePointCache.style)
        pointLayer.updateFeature(this.activePointCache.point)
        this.activePointCache = {
          point: null,
          style: null
        }
      }
      this.popupInstance.setVisibility(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.basic-map {
  height: 100%;
  width: 100%;
}
</style>
